import { SaleCreateInterface } from '../../models/sale-create.interface';
import { CreateSalesApiRequest } from '../../models/requests/crete-sales.request.dto';
import { GlobalStateService } from '@core/services/global.state.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ConversationApiService } from '@core/services/api.services/conversation-api.service';
import { ConversationModel } from '@shared/models/conversation.model';
import { InteractionModel } from '@shared/models/interaction.model';
import { InteractionApiService } from '@core/services/api.services/interaction-api.service';
import {
  ConversationWorkflowModel,
  SearchUserOptionsModel,
  Task,
  WorkflowModel,
  SearchAccountOptionsModel,
  TaskCreateInterface,
  OpportunityCreateModel,
  ConcernCreateModel,
  InteractionCreateInterface,
  ConversationCreateModel,
  WorkflowTemplateModel
} from '@shared/models';
import { ProductApiService, TaskApiService, SalesApiService } from '@core/services/api.services';
import { EmailTemplateDto, PaginatedResponseDto, TaskTemplateDto, UserDto, WorkflowTemplateDto } from '@core/services/dto';
import { WorkflowApiService } from '@core/services/api.services/workflow-api.service';
import { WorkflowTemplateRequest } from '@shared/models/requests/workflow-template.request.dto';
import { UserApiService } from '@core/services/api.services/user-api.service';
import { UserApiRequest } from '@shared/models/requests/user.request.dto';
import { catchError, filter, map } from 'rxjs/operators';
import { TaskTemplateRequest } from '@shared/models/requests/task-template.request.dto';
import { TaskTemplateStatus } from '@shared/models/task-template-status.model';
import { TaskTemplateUseTypeEnum } from '@shared/models/task-template-use-type.model';
import { UserStatusTypeStringEnum } from '@shared/enums/user-status-type.enum';
import { GroupTypeStringEnum } from '@shared/enums/group-type.enum';
import { SMSTemplateDto } from '@core/services/dto/sms-template.dto';

@Injectable({ providedIn: 'any' })

export class CreateConversationStateService {
  private taskTemplates: BehaviorSubject<TaskTemplateDto[]> = new BehaviorSubject([]);
  private emailTemplates: BehaviorSubject<PaginatedResponseDto<EmailTemplateDto>> =
    new BehaviorSubject<PaginatedResponseDto<EmailTemplateDto>>(null);
    private smsTemplates: BehaviorSubject<PaginatedResponseDto<SMSTemplateDto>> =
    new BehaviorSubject<PaginatedResponseDto<SMSTemplateDto>>(null);

  public taskUsers: BehaviorSubject<SearchUserOptionsModel[]> = new BehaviorSubject<SearchUserOptionsModel[]>([]);
  public conversationUsers: BehaviorSubject<SearchUserOptionsModel[]> = new BehaviorSubject<SearchUserOptionsModel[]>([]);
  public taskTemplates$: Observable<TaskTemplateDto[]> = this.taskTemplates.asObservable();
  public emailTemplates$: Observable<PaginatedResponseDto<EmailTemplateDto>> = this.emailTemplates
    .asObservable()
    .pipe(
      filter(x => !!x)
    );
    public smsTemplates$: Observable<PaginatedResponseDto<SMSTemplateDto>> = this.smsTemplates
    .asObservable()
    .pipe(
      filter(x => !!x)
    );
  taskUsers$(): Observable<SearchUserOptionsModel[]> {
    return this.taskUsers.asObservable();
  }

  conversationUsers$(): Observable<SearchUserOptionsModel[]> {
    return this.conversationUsers.asObservable();
  }

  constructor(
    private service: ConversationApiService,
    private globalState: GlobalStateService,
    private interactionService: InteractionApiService,
    private taskService: TaskApiService,
    private workflowService: WorkflowApiService,
    private userService: UserApiService,
    private saleService: SalesApiService
  ) {

  }

  member$(): Observable<any> {
    return this.globalState.memberProfile;
  }

  preselectedAccount$(): Observable<SearchAccountOptionsModel[]> {
    return this.globalState.preselectedAccount;
  }

  getUsers(searchString: string): Observable<PaginatedResponseDto<UserDto>> {
    const request: UserApiRequest = { pageSize: 3, search: searchString };
    return this.userService.getUsers(request);
  }

  getUserGroups(searchString: string): Observable<PaginatedResponseDto<UserDto>> {
    const request: UserApiRequest = { pageSize: null, search: searchString };
    return this.userService.getUserGroups(request);
  }

  getSearchedUsers(search: string, isConversation: boolean): void {
    this.getUsers(search).pipe(

      map((response) => {

        return {
          ...response,
          data: response.data.map(item => new SearchUserOptionsModel(item))
        };
      })
    ).subscribe(response => {
      isConversation ? this.conversationUsers.next(response.data) : this.taskUsers.next(response.data);
    });

  }

  getSearchedUserGroups(search: string, isConversation: boolean): void {
    this.getUserGroups(search).pipe(
      map((response) => {
        const filteredData = response.data.filter(item => {
          // Filter out items with type 'Group' and status 'Inactive'
          if (item.isGroup === true && item.status === 'Inactive') {
            return false;
          }
          return (
            item.type === 'User' ||
            item.type === GroupTypeStringEnum.All ||
            item.type === GroupTypeStringEnum.CRMAssignment ||
            item.type === GroupTypeStringEnum.CRMandECM ||
            item.type === GroupTypeStringEnum.CRMandRepresentativeRole ||
            item.type === GroupTypeStringEnum.RepresentativeRole) &&
            (item.status === UserStatusTypeStringEnum.New ||
            item.status === UserStatusTypeStringEnum.Active);
        });
        return {
          ...response,
          data: filteredData.map(item => new SearchUserOptionsModel(item))
        };
      })
    ).subscribe(response => {
      isConversation ? this.conversationUsers.next(response.data) : this.taskUsers.next(response.data);
    });

  }

  getSearchedWorkflowData(request): Observable<WorkflowTemplateModel[]>{
    return this.getSearchedWorkflowTemplates(request).pipe(
      map((response)=>{
        const data = response.data.map(item => new WorkflowTemplateModel(item));
        return data;
      }),
      catchError((error: any) => {
        return of([]);
      }
    ));
  }

  getSearchedUserGroupsData(search: string): Observable<SearchUserOptionsModel[]> {
    return this.getUserGroups(search).pipe(
      map((response) => {
        const filteredData = response.data.filter(item => {
          // Filter out items with type 'Group' and status 'Inactive'
          if (item.isGroup === true && item.status === 'Inactive') {
            return false;
          }
          // Include both individual Users and Groups (CRM Assignment, All, etc.) with Active or New status
           return (item.type !== 'User' ||
            item.type === 'User') &&  // Added: Include individual users
            (item.status === UserStatusTypeStringEnum.New ||
            item.status === UserStatusTypeStringEnum.Active || item.status === '1');
        });
        // return (item.type === GroupTypeStringEnum.CRMAssignment ||
        //     item.type === GroupTypeStringEnum.All ||
        //     item.type === GroupTypeStringEnum.CRMandRepresentativeRole ||
        //     item.type === GroupTypeStringEnum.CRMandECM) &&
        //     (item.status === UserStatusTypeStringEnum.New ||
        //     item.status === UserStatusTypeStringEnum.Active || item.status === '1');
        // });
        const data = filteredData.map(item => new SearchUserOptionsModel(item));
        return data;
      }),
      catchError((error: any) => {
        return of([]);
      }));
  }

  setConversation(conversation: ConversationCreateModel): Observable<ConversationModel> {
    return this.service.setConversation(conversation);
  }

  setConcern(concern: ConcernCreateModel): Observable<ConversationModel> {
    return this.service.setConcern(concern);
  }

  setOpportunity(opportunity: OpportunityCreateModel): Observable<ConversationModel> {
    return this.service.setOpportunity(opportunity);
  }

  setInteraction(interaction: InteractionCreateInterface): Observable<InteractionModel> {
    return this.interactionService.setInteraction(interaction);
  }

  setTask(task: TaskCreateInterface): Observable<any> {
    return this.taskService.createTask(task);
  }

  setSale(sale: SaleCreateInterface): Observable<any> {
    const request: CreateSalesApiRequest = {
      applicationId: sale.applicationId,
      campaignId: sale.campaignId,
      branchId: sale.branchId,
      referrers: sale.referrers.map(r => r.ref),
      links: sale.links.map(l => ({
        ref: l.ref,
        type: l.type
      })),
      products: sale.products
    };

    return this.saleService.createSale(request);
  }

  getWorkflowTemplates(request: WorkflowTemplateRequest): Observable<PaginatedResponseDto<WorkflowTemplateDto>> {
    return this.workflowService.getWorkflowTemplate(request);
  }

  getSearchedWorkflowTemplates(searchString: string): Observable<PaginatedResponseDto<WorkflowTemplateDto>> {
    const request: WorkflowTemplateRequest = {sortBy: 'name', pageNumber:0, sort:'asc', status:'active', type:'conversations', search: searchString };
    return this.workflowService.getWorkflowTemplate(request);
  }

  createConversationWorkflow(conversationWorkflow: ConversationWorkflowModel, conversationId: number): Observable<WorkflowModel> {
    return this.service.createConversationWorkflow(conversationWorkflow, conversationId);
  }

  getTaskTemplates(): void {
    const request: TaskTemplateRequest = {
      status: TaskTemplateStatus.Active,
      useType: [TaskTemplateUseTypeEnum[TaskTemplateUseTypeEnum.Task], TaskTemplateUseTypeEnum[TaskTemplateUseTypeEnum.All]],
      sortBy: 'name'
    };

    this.taskService.getTaskTemplates(request)
      .subscribe(taskTemplates => {
        this.taskTemplates.next(taskTemplates.data);
      });
  }

  getEmailTemplates(ids: string[]): void {
    if (ids && ids.length) {
      this.taskService.getEmailTemplates(ids)
        .subscribe(data => {
          this.emailTemplates.next(data);
        });
    }
  }
  getsmsTemplates(ids: string[]): void {
    if (ids && ids.length) {
      this.taskService.getSMSTemplates(ids)
        .subscribe(data => {
          this.smsTemplates.next(data);
        });
    }
  }
  getAllSearchedUserGroups(search: string, isConversation: boolean): void {
    this.getUserGroups(search).pipe(
      map((response) => {
        const filteredData = response.data.filter(item => {
          // Filter out items with type 'Group' and status 'Inactive'
          if (item.isGroup === true && item.status === 'Inactive') {
            return false;
          }
          // Include items with types 'CRM Assignment', 'User', or 'All'
          return item.type === 'User' || item.type === GroupTypeStringEnum.All || item.type === GroupTypeStringEnum.CRMandECM ||
          item.type === GroupTypeStringEnum.CRMAssignment || item.type === GroupTypeStringEnum.RepresentativeRole ||
          item.type === GroupTypeStringEnum.CRMandRepresentativeRole || item.type === GroupTypeStringEnum.ECMandRepresentativeRole;
        });
        return {
          ...response,
          data: filteredData.map(item => new SearchUserOptionsModel(item))
        };
      })
    ).subscribe(response => {
      isConversation ? this.conversationUsers.next(response.data) : this.taskUsers.next(response.data);
    });
  }
}
